import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { cameFrom } from './came-from';

/**
 * Where login or signup sends the visitor afterwards (contract with the
 * storefront, bildigsnetz-website#37 step 6 / bildigsnetz-frontend#361):
 *
 * - Query parameter `returnUrl` (`/login?returnUrl=…`, `/signup?returnUrl=…`),
 *   or the matrix parameter the app's own guards write (`/login;returnUrl=…`).
 * - Either an app path (`/chat/new/abc?quelle=chat-zh`) or an absolute https
 *   URL on a storefront host. Any other host is ignored (open-redirect
 *   protection) and the visitor lands where they would without it.
 *
 * The target is kept in localStorage so it survives the Edulog redirect, the
 * email verification and the onboarding, and is used once.
 */
export const RETURN_URL_PARAM = 'returnUrl';

export type ReturnTarget =
  | { kind: 'app'; url: string }
  | { kind: 'storefront'; url: string };

/** The public storefront and its partner platforms (no www: they redirect). */
const STOREFRONT_HOSTS = [
  'bildigsnetz.ch',
  'bildungsstellen.ch',
  'vikariate.ch',
  'stellvertretungen.ch',
];

const STORAGE_KEY = 'returnAfterAuth';
/** Long enough for verification mail and onboarding, short enough to never surprise. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function storefrontHosts(): string[] {
  const own = new URL(environment.homepageBaseUrl).host;
  return [...new Set([own, ...STOREFRONT_HOSTS])];
}

/**
 * The storefront's start page (`/`, or a language root like `/fr`). The
 * website header sends the page it is on, so «Einloggen» on the start page
 * would bring the visitor back there after the login instead of into the app.
 */
const isHomePage = (url: URL) => /^\/([a-z]{2}\/?)?$/.test(url.pathname);

/**
 * A safe target, or null for anything else (other hosts, `//evil`,
 * `javascript:` …) and for a storefront start page: from there the visitor
 * wants the app.
 */
export function parseReturnTarget(
  value: string | null | undefined,
  allowedHosts: string[] = storefrontHosts()
): ReturnTarget | null {
  const raw = value?.trim();
  if (!raw) return null;

  // App path: one leading slash, never `//host` or `/\host` (protocol-relative).
  if (raw.startsWith('/')) {
    return /^\/[/\\]/.test(raw) ? null : { kind: 'app', url: raw };
  }

  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' || url.username || url.password) return null;
    if (!allowedHosts.includes(url.host) || isHomePage(url)) return null;
    return { kind: 'storefront', url: url.toString() };
  } catch {
    return null;
  }
}

/** Whether this navigation came straight from login or signup (see core/came-from.ts). */
export const switchedAuthPage = (router: Router) =>
  cameFrom(router, '/login') || cameFrom(router, '/signup');

/** `returnUrl` of the route: query parameter first, then matrix parameter. */
export function returnUrlOf(route: ActivatedRouteSnapshot): string | null {
  return (
    route.queryParamMap.get(RETURN_URL_PARAM) ??
    route.paramMap.get(RETURN_URL_PARAM)
  );
}

/**
 * Called by login and signup: keeps a valid target, and forgets an old one
 * when the page is opened without any (a new, unrelated login). With
 * `keepExisting` (the visitor switched between login and signup) an old one
 * stays.
 */
export function rememberReturnTarget(
  value: string | null,
  keepExisting = false
): void {
  const target = parseReturnTarget(value);
  if (!target && keepExisting) return;
  try {
    if (target) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...target, savedAt: Date.now() })
      );
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked: the visitor lands on the default page.
  }
}

/** The kept target without using it up. */
export function peekReturnTarget(now = Date.now()): ReturnTarget | null {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as
      | (ReturnTarget & { savedAt: number })
      | null;
    if (!stored || now - stored.savedAt > MAX_AGE_MS) return null;
    // Checked again: storage can be edited.
    return parseReturnTarget(stored.url);
  } catch {
    return null;
  }
}

/** The kept target, used up. */
export function takeReturnTarget(now = Date.now()): ReturnTarget | null {
  const target = peekReturnTarget(now);
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  return target;
}

/**
 * After a login (email, Google, Edulog): goes to the kept target and returns
 * true. An incomplete profile keeps the target and returns false: the caller
 * goes on as before (welcome, onboarding), and the onboarding uses the target
 * at its end.
 */
export async function continueToReturnTarget(
  router: Router,
  profileComplete: () => Promise<boolean>
): Promise<boolean> {
  const target = peekReturnTarget();
  if (!target) return false;
  let complete = false;
  try {
    complete = await profileComplete();
  } catch {
    // No profile yet (first Edulog login): onboarding first.
  }
  if (!complete) return false;
  takeReturnTarget();
  await goToReturnTarget(router, target);
  return true;
}

/** Goes to the target: app paths through the router, storefront pages by full page load. */
export async function goToReturnTarget(
  router: Router,
  target: ReturnTarget
): Promise<void> {
  if (target.kind === 'storefront') {
    window.location.assign(target.url);
    return;
  }
  await router.navigateByUrl(target.url, { replaceUrl: true });
}
